import React, { useEffect, useState, useMemo } from "react";
import { getCompletedBookingsReceipts } from "../../../api/api";
import styles from "./AdminInvoiceList.module.css";

const BookingReceipts = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getCompletedBookingsReceipts();
      if (res && res.success) {
        setData(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching bookings receipts:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return data;
    return data.filter((row) => {
      const bId = String(row.booking_id || "");
      const name = (row.customer_name || "").toLowerCase();
      const phone = (row.customer_phone || "").toLowerCase();
      const odoo = (row.odoo_receipt_number || "").toLowerCase();
      const ref = (row.online_payment_reference || "").toLowerCase();
      const inv = (
        row.payment_option === "online" 
          ? row.online_invoice_number 
          : row.instore_invoice_number
      ) || "";

      return (
        bId.includes(q) ||
        name.includes(q) ||
        phone.includes(q) ||
        odoo.includes(q) ||
        ref.includes(q) ||
        inv.toLowerCase().includes(q)
      );
    });
  }, [data, searchTerm]);

  // Group Completed Bookings helper
  const groupedReceipts = useMemo(() => {
    const groups = [];
    const groupMap = {};

    filteredData.forEach((row) => {
      // Group key: cart_id if present, fallback to odoo_receipt_number, then to online_invoice_number/instore_invoice_number, then to booking_id
      const groupKey = row.cart_id 
        || row.odoo_receipt_number 
        || (row.payment_option === "online" ? row.online_invoice_number : row.instore_invoice_number)
        || `${row.booking_id}-${row.service_kind}`;

      if (!groupMap[groupKey]) {
        groupMap[groupKey] = {
          key: groupKey,
          cart_id: row.cart_id,
          customer_name: row.customer_name,
          customer_phone: row.customer_phone,
          booking_date: row.booking_date,
          payment_option: row.payment_option,
          odoo_receipt_number: row.odoo_receipt_number,
          online_invoice_number: row.online_invoice_number,
          instore_invoice_number: row.instore_invoice_number,
          online_payment_reference: row.online_payment_reference,
          items: [],
        };
        groups.push(groupMap[groupKey]);
      }
      groupMap[groupKey].items.push(row);
    });

    return groups;
  }, [filteredData]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRows = groupedReceipts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(groupedReceipts.length / itemsPerPage);

  return (
    <div className={styles.invoiceContainer}>
      <div className={styles.topBar} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3>Completed Bookings & Receipts</h3>
        <input
          type="text"
          placeholder="Search ID, Name, Odoo Receipt, Invoice, Payment Ref..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid #3c3c28",
            backgroundColor: "#131309",
            color: "#f6f7ee",
            width: "350px",
          }}
        />
      </div>

      {loading ? (
        <p>Loading receipts report...</p>
      ) : (
        <>
          <table className={styles.invoiceTable}>
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Type</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Booking Date</th>
                <th>Total Cost</th>
                <th>Payment Option</th>
                <th>Odoo Receipt</th>
                <th>Payment Reference</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                currentRows.flatMap((group) => {
                  return group.items.map((row, idx) => {
                    const uniqueRowKey = `${row.booking_id}-${row.service_kind}`;

                    // Calculate total cost for the entire group
                    const groupTotalCost = group.items.reduce(
                      (sum, item) => sum + parseFloat(item.total_cost || 0),
                      0
                    );

                    if (idx === 0) {
                      // First item in group: render with rowSpans
                      return (
                        <tr key={uniqueRowKey} style={{ borderTop: "2px solid var(--black-color, #3c3c28)" }}>
                          <td>{row.booking_id}</td>
                          <td style={{ textTransform: "capitalize" }}>{row.service_kind}</td>
                          <td rowSpan={group.items.length}>{group.customer_name}</td>
                          <td rowSpan={group.items.length}>{group.customer_phone || "N/A"}</td>
                          <td rowSpan={group.items.length}>{new Date(group.booking_date).toLocaleDateString()}</td>
                          <td rowSpan={group.items.length}>
                            <span className="dirham-symbol">&#xea;</span> {groupTotalCost.toFixed(2)}
                          </td>
                          <td rowSpan={group.items.length} style={{ textTransform: "capitalize" }}>{group.payment_option}</td>
                          <td rowSpan={group.items.length}>{group.odoo_receipt_number || "-"}</td>
                          <td rowSpan={group.items.length}>{group.online_payment_reference || "-"}</td>
                        </tr>
                      );
                    } else {
                      // Subsequent items in group: render only individual cells
                      return (
                        <tr key={uniqueRowKey}>
                          <td>{row.booking_id}</td>
                          <td style={{ textTransform: "capitalize" }}>{row.service_kind}</td>
                        </tr>
                      );
                    }
                  });
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                    No bookings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #3c3c28",
                  backgroundColor: currentPage === 1 ? "#131309" : "#d2fa10",
                  color: currentPage === 1 ? "#a9aa98" : "#101008",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>
              <span style={{ alignSelf: "center" }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  border: "1px solid #3c3c28",
                  backgroundColor: currentPage === totalPages ? "#131309" : "#d2fa10",
                  color: currentPage === totalPages ? "#a9aa98" : "#101008",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingReceipts;
